/**
 * @file SaveAsNewPlanDialog.jsx
 * @module ui/src/components/tenant-admin/SaveAsNewPlanDialog
 * @description
 *   Forks an existing operational compensation plan into a new independent plan
 *   identity while preserving the source plan unchanged. This operation is deliberately
 *   separate from certified template governance.
 *
 * @version 1.0.0
 * @since S755
 * @author data-fit SME Build · Reviewed: Claude S755
 *
 * @architecture
 *   Locked decisions : #15
 *   Temporal invariants : none
 *   Design principles : DP-10
 *   Cross-cutting contracts : none
 *
 * @domain-rules
 *   - Forking creates a new operational plan identity, version history, and approval lifecycle.
 *   - The source plan remains unchanged by the fork operation.
 *   - Operational plan forking must never be conflated with certified template candidate creation.
 *   - Navigation and success callbacks occur only after the server confirms creation.
 *
 * @session-6-migration-flags
 *   - none
 *
 * @changelog
 *   1.0.0  S755  Initial build — Group D batch delivery
 */

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} SourcePlanSummary
 * @property {number} planId
 * @property {string} planCode
 * @property {string} planName
 * @property {number} version
 * @property {string} planStatus
 */

/**
 * @typedef {Object} SaveAsNewPlanDialogProps
 * @property {SourcePlanSummary} sourcePlan
 * @property {boolean} open
 * @property {(newPlanId: number) => void} onCreated
 * @property {() => void} onCancel
 */

const styles = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    display: 'grid',
    placeItems: 'center',
    background: 'rgb(var(--color-bg))',
    zIndex: 1000,
    padding: '20px',
  },
  dialog: {
    width: 'min(620px, 100%)',
    maxHeight: '90vh',
    overflowY: 'auto',
    padding: '22px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '8px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif',
    fontSize: 'var(--font-sz-base)',
  },
  heading: {
    margin: 0,
    fontSize: '21px',
  },
  muted: {
    color: 'rgb(var(--color-text-muted))',
  },
  field: {
    display: 'grid',
    gap: '6px',
    marginTop: '16px',
  },
  input: {
    width: '100%',
    boxSizing: 'border-box',
    padding: '9px 10px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '5px',
    background: 'rgb(var(--color-surface))',
    color: 'rgb(var(--color-text))',
    font: 'inherit',
  },
  summary: {
    marginTop: '18px',
    padding: '14px',
    border: '1px solid rgb(var(--color-border))',
    borderRadius: '6px',
    background: 'rgb(var(--color-surface-muted))',
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '10px',
    marginTop: '22px',
  },
};

function getFocusableElements(container) {
  // WHY: Focus trapping is based on standard interactive elements so keyboard users
  // cannot tab behind a modal governance surface.
  if (!container) {
    return [];
  }

  return Array.from(
    container.querySelectorAll(
      'button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [href], [tabindex]:not([tabindex="-1"])',
    ),
  );
}

/**
 * @param {SaveAsNewPlanDialogProps} props
 * @returns {React.ReactElement|null}
 */
export default function SaveAsNewPlanDialog({
  sourcePlan,
  open,
  onCreated,
  onCancel,
}) {
  // WHY: Step is presentation workflow state only; no domain mutation occurs until confirm.
  const [step, setStep] = useState('form');

  // WHY: The new operational plan requires its own code rather than inheriting source identity.
  const [newPlanCode, setNewPlanCode] = useState('');

  // WHY: The new operational plan requires its own human-readable name.
  const [newPlanName, setNewPlanName] = useState('');

  // WHY: Pending state blocks duplicate governance mutations while server authority is awaited.
  const [submitting, setSubmitting] = useState(false);

  // WHY: Server failures remain visible on the confirmation step so the user can retry.
  const [error, setError] = useState(null);

  const dialogRef = useRef(null);
  const firstInputRef = useRef(null);
  const headingRef = useRef(null);

  // WHY: Every new opening begins a clean fork workflow and focuses the first required field.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    setStep('form');
    setNewPlanCode('');
    setNewPlanName('');
    setSubmitting(false);
    setError(null);

    const focusTimer = window.setTimeout(() => {
      firstInputRef.current?.focus();
    }, 0);

    return () => {
      window.clearTimeout(focusTimer);
    };
  }, [open]);

  // WHY: Keyboard focus must stay inside the modal, and Escape must map to the explicit
  // parent cancellation path rather than leaving a hidden modal active.
  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      // WHY: Escape is the accessible keyboard equivalent of the cancel control.
      if (event.key === 'Escape') {
        event.preventDefault();
        onCancel();
        return;
      }

      // WHY: Only Tab navigation participates in the focus trap.
      if (event.key !== 'Tab') {
        return;
      }

      const focusable = getFocusableElements(dialogRef.current);
      if (focusable.length === 0) {
        event.preventDefault();
        dialogRef.current?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      // WHY: Shift+Tab from the first item wraps to the final interactive control.
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      }

      // WHY: Tab from the final item wraps to the first interactive control.
      if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [open, onCancel]);

  // WHY: Heading receives focus when switching workflow steps so assistive-technology
  // users receive immediate context for the confirmation state.
  useEffect(() => {
    if (open && step === 'confirm') {
      headingRef.current?.focus();
    }
  }, [open, step]);

  // WHY: Structural form validation is intentionally limited to required identity fields.
  const formValid =
    newPlanCode.trim().length > 0 && newPlanName.trim().length > 0;

  // WHY: Editing code only changes local draft input and clears stale server errors.
  const handlePlanCodeChange = (event) => {
    setNewPlanCode(event.target.value);
    setError(null);
  };

  // WHY: Editing name only changes local draft input and clears stale server errors.
  const handlePlanNameChange = (event) => {
    setNewPlanName(event.target.value);
    setError(null);
  };

  // WHY: Review is a local presentation transition, not the fork mutation itself.
  const handleReview = () => {
    if (!formValid) {
      return;
    }

    setError(null);
    setStep('confirm');
  };

  // WHY: Back returns to editable identity fields without discarding the user's input.
  const handleBack = () => {
    setError(null);
    setStep('form');
  };

  // WHY: Cancellation delegates lifecycle and trigger-focus restoration to the parent.
  const handleCancel = () => {
    if (!submitting) {
      onCancel();
    }
  };

  // WHY: Fork is a governance mutation. The UI waits for the authoritative server response
  // and calls onCreated only after the API returns a concrete newPlanId.
  const handleConfirmFork = async () => {
    if (submitting || !formValid) {
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      // WHY: This endpoint creates a new operational plan and is permanently distinct
      // from template-library candidate creation.
      const response = await fetch(
        `/api/v1/plans/${sourcePlan.planId}/fork`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            planCode:      newPlanCode.trim(),
            planName:      newPlanName.trim(),
            fiscalPeriodId: sourcePlan.fiscalPeriodId,
          }),
        },
      );

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(
          payload?.detail ||
            payload?.message ||
            'The new plan could not be created.',
        );
      }

      const payload = await response.json();

      if (typeof payload.newPlanId !== 'number') {
        throw new Error(
          'The server did not return the new plan identifier.',
        );
      }

      onCreated(payload.newPlanId);
    } catch (requestError) {
      setError(
        requestError instanceof Error
          ? requestError.message
          : 'The new plan could not be created.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  // WHY: A closed controlled dialog must not remain in the document or capture keyboard focus.
  if (!open) {
    return null;
  }

  return (
    <div style={styles.backdrop}>
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="save-as-plan-dialog-title"
        tabIndex={-1}
        style={styles.dialog}
      >
        {/* WHY: Form and confirmation are separate branches so the irreversible mutation
            is preceded by a clear review of the new independent identity. */}
        {step === 'form' ? (
          <>
            <h2 id="save-as-plan-dialog-title" style={styles.heading}>
              Save as new plan
            </h2>
            <p style={styles.muted}>
              Create a new operational plan from {sourcePlan.planName} without
              changing the source plan.
            </p>

            <label style={styles.field}>
              <span>New Plan Code</span>
              <input
                ref={firstInputRef}
                style={styles.input}
                type="text"
                value={newPlanCode}
                onChange={handlePlanCodeChange}
                required
                autoComplete="off"
              />
            </label>

            <label style={styles.field}>
              <span>New Plan Name</span>
              <input
                style={styles.input}
                type="text"
                value={newPlanName}
                onChange={handlePlanNameChange}
                required
                autoComplete="off"
              />
            </label>

            <div style={styles.actions}>
              <button
                type="button"
                className="secondary-button"
                onClick={handleCancel}
              >
                Cancel
              </button>
              <button
                type="button"
                className="primary-button"
                disabled={!formValid}
                onClick={handleReview}
              >
                Review fork
              </button>
            </div>
          </>
        ) : (
          <>
            <h2
              id="save-as-plan-dialog-title"
              ref={headingRef}
              tabIndex={-1}
              style={styles.heading}
            >
              Confirm plan fork
            </h2>

            {/* ── New identity ───────────────────────────────────────── */}
            <div style={styles.summary}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'rgb(var(--color-text-muted))' }}>
                New plan identity
              </p>
              <p style={{ margin: '0 0 4px' }}>
                <strong>Name:</strong> {newPlanName}
              </p>
              <p style={{ margin: '0 0 4px' }}>
                <strong>Code:</strong> {newPlanCode}
              </p>
              <p style={{ margin: 0 }}>
                <strong>Status after fork:</strong> Draft — requires submit &amp; approval
              </p>
            </div>

            {/* ── What is being copied from source ───────────────────── */}
            <div style={{ ...styles.summary, marginTop: '10px' }}>
              <p style={{ margin: '0 0 6px', fontWeight: 600, fontSize: '13px', textTransform: 'uppercase', letterSpacing: '0.04em', color: 'rgb(var(--color-text-muted))' }}>
                Copied from {sourcePlan.planName} (V{sourcePlan.version})
              </p>

              {/* Role + Segment */}
              {(sourcePlan.roleName || sourcePlan.segmentName) && (
                <p style={{ margin: '0 0 4px' }}>
                  <strong>Population:</strong>{' '}
                  {[sourcePlan.roleName, sourcePlan.segmentName].filter(Boolean).join(' · ')}
                </p>
              )}

              {/* Fiscal period */}
              {sourcePlan.fiscalPeriodId && (
                <p style={{ margin: '0 0 4px' }}>
                  <strong>Fiscal period:</strong>{' '}
                  {sourcePlan.fiscalPeriodCode || `ID ${sourcePlan.fiscalPeriodId}`}
                </p>
              )}

              {/* Effective from */}
              {sourcePlan.effectiveFrom && (
                <p style={{ margin: '0 0 4px' }}>
                  <strong>Effective from:</strong> {sourcePlan.effectiveFrom}
                </p>
              )}

              {/* Primary spine */}
              {sourcePlan.primarySpine && (
                <p style={{ margin: '0 0 4px' }}>
                  <strong>Primary spine:</strong> {sourcePlan.primarySpine}
                </p>
              )}

              {/* Weight split from layer1Config */}
              {(() => {
                const w = sourcePlan.layer1Config?.weights;
                if (!w) return null;
                const parts = [];
                if (w.primary  != null && w.primary  > 0) parts.push(`ARR ${w.primary}%`);
                if (w.mbo      != null && w.mbo      > 0) parts.push(`MBO ${w.mbo}%`);
                if (w.nps      != null && w.nps      > 0) parts.push(`NPS ${w.nps}%`);
                if (w.adoption != null && w.adoption > 0) parts.push(`Adoption ${w.adoption}%`);
                if (parts.length === 0) return null;
                return (
                  <p style={{ margin: '0 0 4px' }}>
                    <strong>Weights:</strong> {parts.join(' · ')}
                  </p>
                );
              })()}

              {/* Layer 2 modifiers */}
              {(() => {
                const l2 = sourcePlan.layer2Config;
                if (!Array.isArray(l2) || l2.length === 0) return (
                  <p style={{ margin: '0 0 4px' }}>
                    <strong>Layer 2 modifiers:</strong> None
                  </p>
                );
                return (
                  <p style={{ margin: '0 0 4px' }}>
                    <strong>Layer 2 modifiers:</strong>{' '}
                    {l2.map(m => m.type || m.modifierType || m.name).filter(Boolean).join(', ')}
                  </p>
                );
              })()}

              {/* Components */}
              <p style={{ margin: '0 0 4px' }}>
                <strong>Plan components:</strong> All components and rate tables copied
              </p>
              <p style={{ margin: 0 }}>
                <strong>Assignments:</strong> Not copied — fork starts with 0 assigned positions
              </p>
            </div>

            {/* WHY: Locked consequence copy explicitly protects the distinction between
                unchanged source plan and independent forked plan governance. */}
            <p style={{ marginTop: '14px', marginBottom: '4px' }}>The original plan has not changed.</p>
            <p style={{ margin: 0 }}>
              These plans now have independent identities, version histories
              and approval lifecycles.
            </p>

            {/* WHY: API failure is an inline confirmation-state error, never presented
                as though the local identity form itself failed structural validation. */}
            {error ? (
              <p
                className="modifier-manager-inline-error"
                role="alert"
              >
                {error}
              </p>
            ) : null}

            <div style={styles.actions}>
              <button
                type="button"
                className="secondary-button"
                disabled={submitting}
                onClick={handleBack}
              >
                Back
              </button>
              <button
                type="button"
                className="primary-button"
                disabled={submitting}
                onClick={handleConfirmFork}
              >
                {submitting ? 'Creating plan…' : 'Confirm fork'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

SaveAsNewPlanDialog.propTypes = {
  sourcePlan: PropTypes.shape({
    // Required identity fields
    planId:         PropTypes.number.isRequired,
    planCode:       PropTypes.string.isRequired,
    planName:       PropTypes.string.isRequired,
    version:        PropTypes.number.isRequired,
    planStatus:     PropTypes.string.isRequired,
    // Required for POST body
    fiscalPeriodId: PropTypes.number.isRequired,
    // Optional enrichment fields — used in confirm screen when present
    fiscalPeriodCode: PropTypes.string,
    roleName:       PropTypes.string,
    segmentName:    PropTypes.string,
    primarySpine:   PropTypes.string,
    effectiveFrom:  PropTypes.string,
    layer1Config:   PropTypes.shape({
      weights: PropTypes.shape({
        primary:  PropTypes.number,
        mbo:      PropTypes.number,
        nps:      PropTypes.number,
        adoption: PropTypes.number,
      }),
    }),
    layer2Config:   PropTypes.array,
  }).isRequired,
  open: PropTypes.bool.isRequired,
  onCreated: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

/**
 * @example
 * <SaveAsNewPlanDialog
 *   sourcePlan={{
 *     planId: 418,
 *     planCode: 'AE-ENT-2026',
 *     planName: 'Enterprise AE FY26',
 *     version: 3,
 *     planStatus: 'approved',
 *   }}
 *   open={saveAsOpen}
 *   onCreated={(newPlanId) => navigate(`/plans/${newPlanId}`)}
 *   onCancel={() => setSaveAsOpen(false)}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 * - Forking is an operational plan operation only.
 * - No template-library API or template-governance terminology is used.
 * - Source plan remains explicitly unchanged.
 * - New plan receives independent identity, history, and approval lifecycle.
 * - onCreated executes only after the server confirms a numeric newPlanId.
 * - Mutation uses a disabled pending state and no optimistic navigation.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 * - none
 */

/**
 * ACCESSIBILITY NOTES
 * - Dialog exposes role="dialog", aria-modal, and aria-labelledby.
 * - Focus enters the first required field on open.
 * - Focus moves to the confirmation heading when the step changes.
 * - Tab and Shift+Tab are trapped inside the modal.
 * - Escape invokes the parent cancellation callback.
 * - Parent retains responsibility for restoring focus to the original trigger after close.
 */
